import { BaseMessage, CometChat } from "@cometchat/chat-sdk-javascript";
import {
	CometChatUIKit,
	MessageListStyle,
	DateStyle,
	CometChatUIKitConstants,
	CometChatMessageTemplate,
} from "@cometchat/chat-uikit-react";
import moment from "moment";
import { Conversation } from "../../blocks/chat/src/MessageItemController.web";
import uuid from "uuid"
import { IGroupsPermissionSettings } from "../../blocks/groups/src/GroupsController";
import CometChatBubbleViewTemplate from "./CometChatBubbleViewTemplate";
import React, { Fragment } from "react";
import CometChatBubbleViewAlert from "./CometChatBubbleViewAlert";
import { ISetMessageReply } from "../../blocks/chat/src/ViewChatController";
import { CometChatCalls } from "@cometchat/calls-sdk-javascript";

type TDefinedTemplate = Pick<
	IBubbleViewRendererProps,
	| "userInfo"
	| "checked"
	| "conversationId"
	| "isBlockedByMe"
	| "searchKeyword"
	| "onUnblockUser"
	| "onRefreshMessageList"
	| "onOpenModalTotalReaction"
	| "onSetParentMessageId"
	| "onScrollToBottom"
>;

interface IBubbleViewRendererProps {
	message: CometChat.BaseMessage;
	typeTemplate: string;
	categories: string;
	userInfo: CometChat.User | null;
	checked?: boolean;
	isBlockedByMe?: boolean;
	conversationId: string;
	searchKeyword?: string;
	onUnblockUser?: () => void;
	onRefreshMessageList?: () => void;
	onSetParentMessageId: (messageReplyItem: ISetMessageReply) => void;
	onOpenModalTotalReaction?: (listReaction: IReaction[]) => void;
	onScrollToBottom: () => void;
}

export interface IMetadataConversation {
	username?: string;
	adminApproveNewMembers?: boolean,
	membersPermissions?: {
		addMembers: boolean,
		editSettings: boolean,
		sendMessages: boolean
	};
	user_subscription?: string;
	user_ownership?: boolean
}

export interface IConversation {
	avatar?: string;
	name: string;
	metadata: IMetadataConversation;
	icon?: string;
	scope?: string;
	guid?: string;
	blockedByMe?: boolean;
	uid?: string;
	status?: string;
	hasJoined?: boolean;
}

export interface ReactionItem {
	reaction: string;
	uid: string;
}

export interface ReactionObject {
	reaction: string;
	count: number;
	reactedByMe: boolean;
}

interface IReactedBy {
	hasBlockedMe: boolean;
	blockedByMe: boolean;
	uid: string;
	name: string;
	avatar?: string; // Optional if not all users have an avatar
}

export interface IReaction {
	id: string;
	messageId: string;
	reaction: string;
	uid: string;
	reactedAt: number;
	reactedBy: IReactedBy;
}

export interface ICustomMessageData {
	message?: string,
	type: CUSTOM_MESSAGE_DATA_TYPE
}

interface ICustomMessageCallLink {
	callType: string;
	sessionId: string;
	invitation?: {
		sender: CometChat.User | null;
	};
}

export enum TYPE_CONVERSATION {
	GROUP = "group",
	USER = "user"
}

export enum CUSTOM_MESSAGE_TYPE {
	CUSTOM_ACTION = "action",
	CUSTOM_CONVERSATION = "conversation",
	MEETING = "meeting",
}

export enum CUSTOM_MESSAGE_DATA_TYPE {
	BLOCK = "block",
	UNBLOCK = "unblock",
	CLEAR_CONVERSATION = "clearConversation"
}

export interface MediaData {
	[key: string]: { imageUrl: string, messageId: string }[];
}

export interface DocData {
	[key: string]: { name: string, size: string, pages: string, format: string, date: string, messageId: string }[];
}

export type TTypeConversation = "group" | "user"

export const updateConversationsWhenHasNewMessage = (newMessage: CometChat.TextMessage, prevConversations: Conversation[]) => {
	const isUserMessage = newMessage.getReceiverType() === CometChat.RECEIVER_TYPE.USER
	const conversationId = isUserMessage ? newMessage.getSender().getUid() : newMessage.getReceiverId()
	let updatedConversations = [...prevConversations];
	const conversationIndex = updatedConversations.findIndex(conversation => conversation.id === conversationId);

	if (conversationIndex !== -1) {
		const updatedConversation = updatedConversations[conversationIndex];
		updatedConversation.lastMessage = getMessageContent(newMessage);
		updatedConversation.lastMessageTimestamp = moment.unix(newMessage.getSentAt()).format('HH:mm');
		updatedConversation.unreadMessageCount += 1;

		updatedConversations.splice(conversationIndex, 1);
		updatedConversations.unshift(updatedConversation);
	} else {
		const newConversation: Conversation = {
			id: conversationId,
			type: isUserMessage ? 'USER' : 'GROUP',
			name: isUserMessage ? newMessage.getSender().getName() : newMessage.getReceiver().getName(),
			avatar: isUserMessage ? newMessage.getSender().getAvatar() : '',
			lastMessage: getMessageContent(newMessage),
			lastMessageTimestamp: moment.unix(newMessage.getSentAt()).format('HH:mm'),
			unreadMessageCount: 1,
		};
		updatedConversations.unshift(newConversation);
	}

	return updatedConversations
};

const updateChatWhenSendUserMessage = (
	message: CometChat.TextMessage,
	receiver: CometChat.User,
	prevConversations: Conversation[]
) => {
	let updatedConversations = [...prevConversations];
	const conversationIndex = updatedConversations.findIndex(conversation => conversation.id === message.getReceiverId());
	if (conversationIndex !== -1) {
		const updatedConversation = updatedConversations[conversationIndex];
		updatedConversation.lastMessage = getMessageContent(message);
		updatedConversation.lastMessageTimestamp = moment.unix(message.getSentAt()).format('HH:mm');
		updatedConversation.unreadMessageCount = 0;

		updatedConversations.splice(conversationIndex, 1);
		updatedConversations.unshift(updatedConversation);
	} else {
		const newConversation: Conversation = {
			id: receiver.getUid(),
			type: 'USER',
			name: receiver.getName(),
			avatar: receiver.getAvatar(),
			lastMessage: getMessageContent(message),
			lastMessageTimestamp: moment.unix(message.getSentAt()).format('HH:mm'),
			unreadMessageCount: 0,
		};
		updatedConversations.unshift(newConversation);
	}
	return updatedConversations
}

const updateChatWhenSendGroupMessage = (
	message: CometChat.TextMessage,
	receiver: CometChat.Group,
	prevConversations: Conversation[]
) => {
	let updatedConversations = [...prevConversations]
	const conversationIndex = updatedConversations.findIndex(conversation => conversation.id === message.getReceiverId())
	if (conversationIndex !== -1) {
		const updatedConversation = updatedConversations[conversationIndex]
		updatedConversation.unreadMessageCount = 0
		updatedConversation.lastMessageTimestamp = moment.unix(message.getSentAt()).format('HH:mm')
		updatedConversation.lastMessage = getMessageContent(message);

		updatedConversations.splice(conversationIndex, 1)
		updatedConversations.unshift(updatedConversation)
	} else {
		const newConversation: Conversation = {
			id: receiver.getGuid(),
			type: 'GROUP',
			name: receiver.getName(),
			avatar: receiver.getIcon(),
			unreadMessageCount: 0,
			lastMessageTimestamp: moment.unix(message.getSentAt()).format('HH:mm'),
			lastMessage: getMessageContent(message),
		};
		updatedConversations.unshift(newConversation);
	}

	return updatedConversations
}

export const updateMessageTemplates = ({
	userInfo,
	checked,
	isBlockedByMe,
	conversationId,
	searchKeyword,
	onRefreshMessageList,
	onUnblockUser,
	onOpenModalTotalReaction,
	onSetParentMessageId,
	onScrollToBottom,
}: TDefinedTemplate) => {
	// Get all message templates
	let definedTemplates = CometChatUIKit.getDataSource().getAllMessageTemplates();

	// Define type templates to override
	const typeTemplatesOverride = [
		CometChatUIKitConstants.MessageTypes.text,
		CometChatUIKitConstants.MessageTypes.image,
		CometChatUIKitConstants.MessageTypes.file,
		CometChatUIKitConstants.MessageTypes.groupMember,
		CometChatUIKitConstants.MessageTypes.audio,
	];

	// Define categories templates to override
	const categoriesTemplatesOverride = [
		CometChatUIKitConstants.MessageCategory.message,
		CometChatUIKitConstants.MessageCategory.action,
		CometChatUIKitConstants.MessageCategory.call,
	];

	// Override existing templates
	typeTemplatesOverride.forEach(typeTemplate => {
		categoriesTemplatesOverride.forEach(categoryTemplate => {
			const messageTemplates = definedTemplates.find(
				(template) =>
					categoryTemplate === template.category &&
					template.type === typeTemplate
			);
			
			if (!messageTemplates) return;
	
			const textTemplateObjectIndex = definedTemplates.indexOf(messageTemplates);
	
			messageTemplates.bubbleView = (message: CometChat.BaseMessage) =>
				getBubbleViewRenderer({
					message,
					typeTemplate,
					categories: messageTemplates.category,
					userInfo: userInfo,
					checked: checked,
					conversationId,
					searchKeyword,
					onRefreshMessageList,
					onOpenModalTotalReaction,
					onSetParentMessageId,
					onScrollToBottom,
				});
	
			if (textTemplateObjectIndex > -1) {
				definedTemplates.splice(textTemplateObjectIndex, 1, messageTemplates);
			}
		})
	});

	// Add a custom message template
	const customMessageTemplate = new CometChatMessageTemplate({
		type: CUSTOM_MESSAGE_TYPE.CUSTOM_ACTION,
		category: CometChatUIKitConstants.MessageCategory.custom,
		bubbleView: (message: CometChat.BaseMessage) =>
			getBubbleViewRenderer({
				message,
				typeTemplate: CUSTOM_MESSAGE_TYPE.CUSTOM_ACTION,
				categories: CometChatUIKitConstants.MessageCategory.custom,
				userInfo: userInfo,
				checked: checked,
				isBlockedByMe,
				conversationId,
				onUnblockUser,
				onSetParentMessageId,
				onScrollToBottom,
			}),
	});

	const customMessageClearConversation = new CometChatMessageTemplate({
		type: CUSTOM_MESSAGE_TYPE.CUSTOM_CONVERSATION,
		category: CometChatUIKitConstants.MessageCategory.custom,
		bubbleView: () => (<Fragment/>) 
	});

	const customMessageMeetingLink = new CometChatMessageTemplate({
		type: CUSTOM_MESSAGE_TYPE.MEETING,
		category: CometChatUIKitConstants.MessageCategory.custom,
		bubbleView: (message: CometChat.BaseMessage) =>
			getBubbleViewRenderer({
				message,
				typeTemplate: CUSTOM_MESSAGE_TYPE.MEETING,
				categories: CometChatUIKitConstants.MessageCategory.custom,
				userInfo: userInfo,
				checked: checked,
				conversationId,
				onSetParentMessageId,
				onScrollToBottom,
			}),
	});

	definedTemplates.push(customMessageTemplate, customMessageClearConversation, customMessageMeetingLink);

	return definedTemplates;
}

export const getListUserFriendComet = async ({
	searchKeyword
}: { searchKeyword?: string }) => {
	let limit: number = 30;
	let usersRequestBuilder = new CometChat.UsersRequestBuilder()
		.setLimit(limit)
		.friendsOnly(true)
		.hideBlockedUsers(true)
	if (searchKeyword)
		usersRequestBuilder.setSearchKeyword(searchKeyword);
	const usersRequest = usersRequestBuilder.build();
	const listUser = await usersRequest.fetchNext();
	return listUser
}

export const sendTextMessage = async (
	messageText: string,
	selectedConversation: CometChat.User | CometChat.Group | null,
	prevConversations: Conversation[],
	duration: number,
	parentMessageIdReply?: number | null
) => {
	if (!selectedConversation) return

	let textMessage: CometChat.TextMessage
	let receiverID: string
	let updatedConversations: Conversation[] = []

	try {

		const expireAt = Date.now() + duration
		if (selectedConversation instanceof CometChat.User) {

			receiverID = selectedConversation.getUid()
			textMessage = new CometChat.TextMessage(receiverID, messageText, CometChat.RECEIVER_TYPE.USER)
			if (parentMessageIdReply) textMessage.setParentMessageId(parentMessageIdReply);
			const receiver = await CometChat.getUser(receiverID)
			const messageData = await CometChatUIKit.sendTextMessage(textMessage) as unknown as CometChat.TextMessage;
			if (duration) {
				await CometChat.callExtension('disappearing-messages', 'DELETE', 'v1/disappear', {
					msgId: messageData.getId(),
					timeInMS: expireAt,
				});
			}

			updatedConversations = updateChatWhenSendUserMessage(messageData, receiver, prevConversations);

		} else {
			receiverID = selectedConversation.getGuid()
			textMessage = new CometChat.TextMessage(receiverID, messageText, CometChat.RECEIVER_TYPE.GROUP)
			textMessage.setMetadata({ expireAt });
			if (parentMessageIdReply) textMessage.setParentMessageId(parentMessageIdReply);
			const receiver = await CometChat.getGroup(receiverID);
			const messageData = await CometChatUIKit.sendTextMessage(textMessage) as unknown as CometChat.TextMessage;

			if (duration) {
				await CometChat.callExtension('disappearing-messages', 'DELETE', 'v1/disappear', {
					msgId: messageData.getId(),
					timeInMS: expireAt,
				});
			}

			updatedConversations = updateChatWhenSendGroupMessage(messageData, receiver, prevConversations);
		}
	} catch (error) {
		console.log('Send text message failed with error', error);
	}

	return updatedConversations
}
export const sendMediaMessage = async (
	files: File[],
	selectedConversation: CometChat.User | CometChat.Group | null,
	prevConversations: Conversation[],
	duration: number,
	parentMessageIdReply?: number | null
) => {
	if (!selectedConversation) return

	let receiverID: string
	let updatedConversations: Conversation[] = []

	const isTypeImage = files[0].type.startsWith('image/');
	const mediaMessageType = isTypeImage ? CometChat.MESSAGE_TYPE.IMAGE : CometChat.MESSAGE_TYPE.FILE;
	let receiverType = CometChat.RECEIVER_TYPE.USER;
	try {
		const expireAt = Date.now() + duration;
		if (selectedConversation instanceof CometChat.User) {
			receiverID = selectedConversation.getUid()
			const mediaMessage = new CometChat.MediaMessage(
				receiverID,
				files,
				mediaMessageType,
				receiverType
			);
			if (parentMessageIdReply) mediaMessage.setParentMessageId(parentMessageIdReply);
			const receiver = await CometChat.getUser(receiverID)
			const messageData = await CometChatUIKit.sendMediaMessage(mediaMessage) as unknown as CometChat.TextMessage;
			if (duration) {
				await CometChat.callExtension('disappearing-messages', 'DELETE', 'v1/disappear', {
					msgId: messageData.getId(),
					timeInMS: expireAt,
				});
			}
			updatedConversations = updateChatWhenSendUserMessage(messageData, receiver, prevConversations);
		} else {
			receiverID = selectedConversation.getGuid();
			receiverType = CometChat.RECEIVER_TYPE.GROUP;
			const mediaMessage = new CometChat.MediaMessage(
				receiverID,
				files,
				mediaMessageType,
				receiverType
			);
			if (parentMessageIdReply) mediaMessage.setParentMessageId(parentMessageIdReply);
			const receiver = await CometChat.getGroup(receiverID);
			const messageData = await CometChatUIKit.sendMediaMessage(mediaMessage) as unknown as CometChat.TextMessage;
			if (duration) {
				await CometChat.callExtension('disappearing-messages', 'DELETE', 'v1/disappear', {
					msgId: messageData.getId(),
					timeInMS: expireAt,
				});
			}
			updatedConversations = updateChatWhenSendGroupMessage(messageData, receiver, prevConversations);
		}
	} catch (error) {
		console.log('Send media message failed with error', error);
	}

	return updatedConversations
}

export const sendCustomMessage = async (
	receiverID: string,
	receiverType: TTypeConversation,
	customData: ICustomMessageData | ICustomMessageCallLink,
	customType: CUSTOM_MESSAGE_TYPE
) => {
	try {
		let customMessage: CometChat.CustomMessage = new CometChat.CustomMessage(
			receiverID,
			receiverType,
			customType,
			customData
		);
		await CometChatUIKit.sendCustomMessage(customMessage);
	} catch (err) {
		console.error("Send custom message failed with error", err)
	}
}

export const sendCallLinkChatMessage = async (
	receiverID: string,
	receiverType: TTypeConversation,
	sessionId: string
) => {
	if (receiverType === CometChat.RECEIVER_TYPE.GROUP) {
		const customData = {
			callType: "audio",
			sessionId,
		};
		await sendCustomMessage(receiverID, receiverType, customData, CUSTOM_MESSAGE_TYPE.MEETING);
	}
}

export const startTypingMessage = async (
	selectedConversation: CometChat.User | CometChat.Group | null,
) => {
	if (!selectedConversation) return;

	let receiverType: string = CometChat.RECEIVER_TYPE.USER;
	let receiverId: string

	try {
		if (selectedConversation instanceof CometChat.User) {
			receiverId = selectedConversation.getUid()
		} else {
			receiverId = selectedConversation.getGuid()
		}
		let typingNotification: CometChat.TypingIndicator = new CometChat.TypingIndicator(receiverId, receiverType);
		CometChat.startTyping(typingNotification);

	} catch (error) {
		console.log('Start typing text message failed with error', error);
	}
}

export const stopTypingMessage = async (
	selectedConversation: CometChat.User | CometChat.Group | null,
) => {
	if (!selectedConversation) return;

	let receiverType: string = CometChat.RECEIVER_TYPE.USER;
	let receiverId: string

	try {
		if (selectedConversation instanceof CometChat.User) {
			receiverId = selectedConversation.getUid()
		} else {
			receiverId = selectedConversation.getGuid()
		}
		let typingNotification: CometChat.TypingIndicator = new CometChat.TypingIndicator(receiverId, receiverType);
		CometChat.endTyping(typingNotification);

	} catch (error) {
		console.log('Stop typing text message failed with error', error);
	}
}

export const addReactionToMessage = async (messageId: number, emoji: string) => {
	try {
		await CometChat.addReaction(messageId, emoji);
	}
	catch (err) {
		console.error("Send reaction fail")
	}
}

export const removeReactionToMessage = async (messageId: number, emoji: string) => {
	try {
		await CometChat.removeReaction(messageId, emoji);
	}
	catch (err) {
		console.error("Send reaction fail")
	}
}

export const deleteMessage = async (messageId: string) => {
	try {
		await CometChat.deleteMessage(messageId);
	} catch (err) {
		console.error("Delete message failed with error ", err)
	}
}

export const fetchConversations = async (conversationType?: TTypeConversation) => {
	const limit = 30;
	let conversationRequest = new CometChat.ConversationsRequestBuilder()
		.setLimit(limit)
		.includeBlockedUsers(true)
	if(conversationType)
		conversationRequest.setConversationType(conversationType)

	try {
		const conversationList: CometChat.Conversation[] = await conversationRequest.build().fetchNext();
		if (!conversationList.length)
			return [];
		const formattedConversations: Conversation[] = conversationList.map((conversation: CometChat.Conversation) => {
			const lastMessage = conversation.getLastMessage();
			const lastMessageText = getMessageContent(lastMessage)
			const lastMessageTime = lastMessage ? moment.unix(lastMessage.getSentAt()).format('HH:mm') : ''
			const conversationtype = conversation.getConversationType()
			const isUser = conversationtype === TYPE_CONVERSATION.USER;

			if (isUser) {
				const user = conversation.getConversationWith() as CometChat.User;
				return {
					id: user.getUid(),
					type: 'USER',
					name: user.getName(),
					avatar: user.getAvatar(),
					lastMessage: lastMessageText,
					lastMessageTimestamp: lastMessageTime,
					unreadMessageCount: conversation.getUnreadMessageCount(),
				};
			} else {
				const group = conversation.getConversationWith() as CometChat.Group
				return {
					id: group.getGuid(),
					type: 'GROUP',
					name: group.getName(),
					avatar: group.getIcon(),
					lastMessage: lastMessageText,
					lastMessageTimestamp: lastMessageTime,
					unreadMessageCount: conversation.getUnreadMessageCount(),
				}
			}
		});

		return formattedConversations;
	} catch (error) {
		console.log("Conversations list fetching failed with error:", error);
		return [];
	}
};

export const fetchGroupSubscribe = async () => {
	const limit = 30;
	let tags = ["paid"];
	let conversationRequest = new CometChat.GroupsRequestBuilder()
		.setLimit(limit)
		.setTags(tags)
		.build();
	try {
		const conversationList: CometChat.Group[] = await conversationRequest.fetchNext();
		if (!conversationList.length)
			return [];
		const formattedConversations: Conversation[] = conversationList.map((conversation: CometChat.Group) => {
			const lastMessageText = "";
			const lastMessageTime = "";
			const conversationtype = "GROUP"

			return {
				id: conversation.getGuid(),
				type: conversationtype,
				name: conversation.getName(),
				avatar: conversation.getIcon(),
				lastMessage: lastMessageText,
				lastMessageTimestamp: lastMessageTime,
				unreadMessageCount: 0,
			}
		});

		return formattedConversations;
	} catch (error) {
		console.log("Group Subscribe list fetching failed with error:", error);
		return [];
	}
};

export const fetchUsers = async (listReceiverUid: string[]) => {
	const limit = 30;
	const usersRequest = new CometChat.UsersRequestBuilder()
		.setLimit(limit)
		.setUIDs(listReceiverUid)
		.build();
	try {
		const response = await usersRequest.fetchNext();
		return response
	} catch (error) {
		console.log("User list fetching failed with error:", error);
		return [];
	}
};

export const fetchCallHistory = async (authToken: string, status?: ("ongoing" | "busy" | "rejected" | "cancelled" | "ended" | "missed")) => {
	let callLogRequestBuilder = new CometChatCalls.CallLogRequestBuilder()
		.setLimit(30)
		.setAuthToken(authToken)
		.setCallCategory("call")

	if(status) {
		callLogRequestBuilder.setCallStatus(status)
		callLogRequestBuilder.setCallDirection("incoming")
	}
	const callLogRequestQuery = callLogRequestBuilder.build();
	try {
		const response = await callLogRequestQuery.fetchNext();
		return response
	} catch (error) {
		console.log("Call history fetching failed with error:", error);
		return [];
	}
}

const fetchMessageItemByMessageId = async (
	messageId: number,
	receiverType: string,
	receiverId: string
): Promise<CometChat.BaseMessage[]> => {
	try {
		const limit = 1;
		const messagesRequestBuilder = new CometChat.MessagesRequestBuilder()
			.setLimit(limit)
			.setMessageId(++messageId) // Increment messageId to fetch the next message
			.hideReplies(false);

		if (receiverType === "group") {
			messagesRequestBuilder.setGUID(receiverId);
		} else {
			messagesRequestBuilder.setUID(receiverId);
		}

		const messagesRequest = messagesRequestBuilder.build();
		return await messagesRequest.fetchPrevious();
	} catch (err) {
		console.error("Error fetching message by ID:", err);
		return [];
	}
};

export const fetchMessageInConversation = async (
	receiverId: string, receiverType: string
) => {
	try {
		const messagesRequestBuilder = new CometChat.MessagesRequestBuilder()
			.hideDeletedMessages(true)
			.hideReplies(false)
			.setLimit(10);
		if (receiverType === "group") {
			messagesRequestBuilder.setGUID(receiverId)
		} else messagesRequestBuilder.setUID(receiverId)

		const messagesRequest = messagesRequestBuilder.build();

		const listMessage = await messagesRequest.fetchPrevious();
		return listMessage
	} catch (err) {
		console.error("Fetch message in conversation failed with err ", err)
		return []
	}
}

export const fetchMediaMessages = async (
	conversationId: string,
	isGroup: boolean,
	limit: number = 50
): Promise<{ media: MediaData; docs: DocData }> => {

	const MEDIA_MESSAGE_TYPES = ["image", "video", "audio", "file"];
	const categories: Array<String> = ["message", "custom"];
	const messagesRequest = new CometChat.MessagesRequestBuilder()
		.setLimit(limit)
		.setCategories(categories)
		.setTypes(MEDIA_MESSAGE_TYPES);

	if (isGroup) {
		messagesRequest.setGUID(conversationId);
	} else {
		messagesRequest.setUID(conversationId);
	}

	const media: MediaData = {};
	const docs: DocData = {};

	try {
		const messages = await messagesRequest.build().fetchPrevious();

		messages.forEach((message) => {
			const month = new Date(message.getSentAt() * 1000).toLocaleString("default", {
				month: "long",
			}).toLowerCase();

			const messageId = message.getId().toString(); // Get the message ID

			if (message.getType() === "image") {
				const attachments = message.getData().attachments || [];
				attachments.forEach((attachment: any, index: number) => {
					const imageUrl = attachment.url;
					if (imageUrl) {
						if (!media[month]) media[month] = [];
						media[month].push({
							imageUrl,
							messageId: `${messageId}-${index}`, // Use the same messageId for all attachments in this message
						});
					}
				});
			} else if (message.getType() === "file") {
				const fileData = message.getData().attachments?.[0];
				if (fileData) {
					if (!docs[month]) docs[month] = [];
					docs[month].push({
						name: fileData.name,
						pages: "1", // Parse page count from metadata if available
						size: `${(fileData.size / 1024).toFixed(2)} kB`,
						format: fileData.extension.toUpperCase(),
						date: new Date(message.getSentAt() * 1000).toLocaleString(),
						messageId, // Add messageId here
					});
				}
			}
		});

		return { media, docs };
	} catch (error) {
		console.error("Error fetching media messages:", error);
		return { media: {}, docs: {} };
	}
};


export const fetchThreadedMessage = async (
	parentMessageId: number,
	receiverType: string,
	receiverId: string
): Promise<CometChat.BaseMessage[]> => {
	try {
		const limit = 50;
		const messagesRequestBuilder = new CometChat.MessagesRequestBuilder()
			.setLimit(limit)
			.setParentMessageId(parentMessageId)
			.hideReplies(false);

		if (receiverType === "group") {
			messagesRequestBuilder.setGUID(receiverId);
		} else {
			messagesRequestBuilder.setUID(receiverId);
		}

		const messagesRequest = messagesRequestBuilder.build();
		return await messagesRequest.fetchPrevious();
	} catch (err) {
		console.error("Error fetching threaded messages:", err);
		return [];
	}
};

export const fetchListReactionByMessage = async (messageId: number) => {
	try {
		let limit = 10;
		let reactionRequest = new CometChat.ReactionsRequestBuilder()
			.setMessageId(messageId)
			.setLimit(limit)
			.build();

		const listReaction = await reactionRequest.fetchNext();
		return listReaction as unknown as ReactionItem[];
	} catch (err) {
		console.error("Fetch list reaction by message id fail", err)
		return [];
	}
}

export const getReplyMessageItem = async (
	message: CometChat.BaseMessage,
	conversationId: string
): Promise<CometChat.BaseMessage | null> => {
	const parentMessageId = message.getParentMessageId();
	const receiverType = message.getReceiverType();
	const messageId = message.getId();

	if (!parentMessageId) return null;

	try {
		const listMessageInThread = await fetchThreadedMessage(
			parentMessageId,
			receiverType,
			conversationId
		);

		for (let [index, messageItem] of listMessageInThread.entries()) {
			if (messageItem.getId() === messageId) {
				if (index === 0) {
					const parentMessages = await fetchMessageItemByMessageId(
						parentMessageId,
						receiverType,
						conversationId
					);
					return parentMessages[0] || null;
				}
				return listMessageInThread[index - 1];
			}
		}
	} catch (err) {
		console.error("Error fetching reply message item:", err);
	}
	return null;
};

export const getMessageContent = (message: CometChat.TextMessage | CometChat.MediaMessage | CometChat.CustomMessage) => {
	if (!message) return "New conversation"
	if (message.getDeletedAt()) return "This message was deleted";
	if (message.getReceiverType() === CometChat.RECEIVER_TYPE.GROUP && message.getCategory() === CometChat.CATEGORY_ACTION) {
		const castMessage = message as unknown as { message: string };
		return castMessage.message
	}
	const mediaFileType = message.getData().type;
	if (message instanceof CometChat.TextMessage) {
		return message.getText()
	}
	if (message instanceof CometChat.MediaMessage) {
		return `Sent an ${mediaFileType}`
	}
	if (message instanceof CometChat.CustomMessage) {
		if (!message?.getCustomData())
			return "Have an new message"
		const { type = "" } = message?.getCustomData() as ICustomMessageData;
		if (type === CUSTOM_MESSAGE_DATA_TYPE.BLOCK || type === CUSTOM_MESSAGE_DATA_TYPE.UNBLOCK)
			return "Have a new action";
		if (type === CUSTOM_MESSAGE_DATA_TYPE.CLEAR_CONVERSATION){
			if(localStorage.getItem("cometChatToken")?.includes(message.getSender().getUid())) return "The chat has already been cleared";
			return `${message.getSender().getName()} has cleared their chat`
		}
	}
	return "Have an new message"
}

export const getMessageFromConversation = async (idTypeConversation: string, typeConversation: TTypeConversation) => {
	let limit = 30;
	let messagesRequest;
	try {
		if (typeConversation.toLowerCase() === TYPE_CONVERSATION.USER) {
			messagesRequest = new CometChat.MessagesRequestBuilder()
				.setUID(idTypeConversation)
				.setLimit(limit)
				.build();
		} else {
			messagesRequest = new CometChat.MessagesRequestBuilder()
				.setGUID(idTypeConversation)
				.setLimit(limit)
				.build();
		}

		const listMessage = await messagesRequest.fetchPrevious();
		return listMessage;
	} catch (error) {
		console.error("Message fetching failed with error:", error);
		return [];
	}
}

export const getMessageRequestBuilder = () => {
	let categories = CometChatUIKit.getDataSource().getAllMessageCategories();
	categories.push(CometChatUIKitConstants.MessageCategory.custom);

	let types = CometChatUIKit.getDataSource().getAllMessageTypes();
	types.push(CUSTOM_MESSAGE_TYPE.CUSTOM_ACTION);

	return new CometChat.MessagesRequestBuilder()
		.setCategories(categories)
		.setTypes(types)
		.hideDeletedMessages(true)
		.hideReplies(false)
		.setLimit(20);
}

export const getMessageRequestBuilderSearchChat = (message: BaseMessage | null, receiverId: string, receiverType: string) => {
	// Get next messageId;
	let messageId = message?.getId() || 0;
	let categories = CometChatUIKit.getDataSource().getAllMessageCategories();
	categories.push(CometChatUIKitConstants.MessageCategory.custom);

	let types = CometChatUIKit.getDataSource().getAllMessageTypes();
	types.push(CUSTOM_MESSAGE_TYPE.CUSTOM_ACTION);

	const messagesRequestBuilder = new CometChat.MessagesRequestBuilder()
		.setCategories(categories)
		.setMessageId(++messageId)
		.setTypes(types)
		.hideDeletedMessages(true)
		.hideReplies(false)
		.setLimit(8);

	if (receiverType === "group") messagesRequestBuilder.setGUID(receiverId)
	else messagesRequestBuilder.setUID(receiverId)

	return messagesRequestBuilder;
}

export const getMessageBySearchKeyword = async (searchKeyword: string, receiverId: string, receiverType?: string) => {
	let listMessages: BaseMessage[] = [];
	try {
		const limit = 30;
		const messagesRequestBuilder = new CometChat.MessagesRequestBuilder()
			.setLimit(limit)
			.setSearchKeyword(searchKeyword)
			.setTypes([CometChat.MESSAGE_TYPE.TEXT])

		if (receiverType === "group") {
			messagesRequestBuilder.setGUID(receiverId)
		} else messagesRequestBuilder.setUID(receiverId)

		const messagesRequest = messagesRequestBuilder.build();
		listMessages = await messagesRequest.fetchPrevious();
	} catch (err) {
		console.error("Get message by search keyword failed with err", err)
	}
	return listMessages;
}

export const getConversationAvatar = (conversation: CometChat.User | CometChat.Group) => {
	if (conversation instanceof CometChat.User) {
		return conversation.getAvatar()
	} else {
		return conversation.getIcon()
	}
}

const getBubbleViewRenderer = ({
	message,
	typeTemplate,
	categories,
	userInfo,
	checked,
	isBlockedByMe = false,
	conversationId,
	searchKeyword,
	onRefreshMessageList,
	onUnblockUser,
	onOpenModalTotalReaction,
	onSetParentMessageId,
	onScrollToBottom
}: IBubbleViewRendererProps) => {
	const sender = message?.getSender();
	const messageId = message?.getId();

	if (!sender || !messageId) {
		return <Fragment />;
	}

	if (message?.getDeletedAt() || message?.getDeletedBy()) {
		return <Fragment />;
	}

	if (categories === CometChatUIKitConstants.MessageCategory.custom && typeTemplate === CUSTOM_MESSAGE_TYPE.CUSTOM_ACTION)
		return <CometChatBubbleViewAlert
			isBlockedByMe={isBlockedByMe}
			onUnblockUser={onUnblockUser}
			message={message}
			userInfo={userInfo}
			checked={checked}
		/>;

	return <CometChatBubbleViewTemplate
		message={message}
		typeTemplate={typeTemplate}
		categories={categories}
		userInfo={userInfo}
		checked={checked}
		conversationId={conversationId}
		searchKeyword={searchKeyword}
		onRefreshMessageList={onRefreshMessageList}
		onOpenModalTotalReaction={onOpenModalTotalReaction}
		onSetParentMessageId={onSetParentMessageId}
		onScrollToBottom={onScrollToBottom}
	/>;
}

export const createGroupChat = async ({
	listMemberUid,
	groupName,
	iconGroup,
	description,
	onClose,
	metadata
}: {
	listMemberUid: string[],
	groupName: string,
	iconGroup: string | undefined,
	description: string,
	onClose: () => void;
	metadata?: IGroupsPermissionSettings;
}) => {
	try {
		let newConversation: CometChat.Group | Object;
		const descriptionValue = description ? description : undefined;
		const members: Array<CometChat.GroupMember> = listMemberUid.map(uid => new CometChat.GroupMember(uid, CometChat.GROUP_MEMBER_SCOPE.MODERATOR))
		let GUID: string = uuid.v4().toString();
		let groupType: string = CometChat.GROUP_TYPE.PUBLIC;
		let groupWithMember = new CometChat.Group(GUID, groupName, groupType, undefined, iconGroup, descriptionValue);
		var groupWithoutMember = new CometChat.Group(GUID, groupName, groupType, undefined, iconGroup, descriptionValue);
		let banMembers: Array<string> = [];
		if (metadata) {
			groupWithMember.setMetadata(metadata);
			groupWithoutMember.setMetadata(metadata);
		}
		if (!members.length) {
			newConversation = await CometChat.createGroup(groupWithoutMember)
		} else {
			newConversation = await CometChat.createGroupWithMembers(groupWithMember, members, banMembers);
		}
		onClose();
		return newConversation;
	} catch (err) {
		console.error("Create group be error", err)
		return {}
	}

}

export const addMembersGroup = async ({
	guid,
	listMember,
}: {
	guid: string;
	listMember: CometChat.User[];
}) => {
	const members: Array<CometChat.GroupMember> = listMember.map(member => new CometChat.GroupMember(member.getUid(), CometChat.GROUP_MEMBER_SCOPE.MODERATOR))
	try {
		const response = await CometChat.addMembersToGroup(guid, members, [])
		return response;
	} catch (err) {
		console.error("Add member in group failed with error", err)
	}
}

export const removeMemberGroup = async ({
	guid,
	uid,
}: {
	guid: string;
	uid: string;
}) => {
	try {
		const response = await CometChat.kickGroupMember(guid, uid);
		return response;
	} catch (err) {
		console.error("Remove member in group failed with error", err)
	}
}

export const blockUsers = async (usersList: string[]) => {
	try {
		await CometChat.blockUsers(usersList);
	} catch (err) {
		console.error("Block user failing", err)
	}
}

export const unblockUsers = async (usersList: string[]) => {
	try {
		await CometChat.unblockUsers(usersList);
	} catch (err) {
		console.error("Block user failing", err)
	}
}

export const leaveGroup = async (guid: string) => {
	try {
		await CometChat.leaveGroup(guid);
	} catch (err) {
		console.error("Leaver a group fail", err)
	}
}

export const clearConversation = async (conversation: CometChat.User | CometChat.Group | null) => {
	if (!conversation) return;

	let type: TTypeConversation;
	let receiverId: string;
	const customData = {
		type: CUSTOM_MESSAGE_DATA_TYPE.CLEAR_CONVERSATION
	}
	try {
		if (conversation instanceof CometChat.User) {
			receiverId = conversation.getUid()
			type = "user";
		} else {
			receiverId = conversation.getGuid()
			type = "group";
		}
		await CometChat.deleteConversation(receiverId, type);
		await sendCustomMessage(receiverId, type, customData, CUSTOM_MESSAGE_TYPE.CUSTOM_CONVERSATION)

	} catch (error) {
		console.log('Error while deleting a conversation', error);
	}
}

export const updateGroupData = async (groupId: string, {
	name,
	icon,
	description
}: {
	name?: string;
	icon?: string;
	description?: string;
}) => {
	try {
		const group = await CometChat.getGroup(groupId);
		if (name) group.setName(name);
		if (icon) group.setIcon(icon);
		if (description) group.setDescription(description);

		await CometChat.updateGroup(group);
	} catch (error) {
		console.error("Error updating group metadata", error);
	}
};

export const getGroupData = async (groupId: string) => {
	try {
		const group = await CometChat.getGroup(groupId);
		return group;
	} catch (error) {
		console.error("Error fetching group metadata", error);
		return null;
	}
};

export const getGroupMembers = async (groupId: string, limit?: number) => {
	const scope = [CometChat.GROUP_MEMBER_SCOPE.PARTICIPANT, CometChat.GROUP_MEMBER_SCOPE.MODERATOR]
	if (!limit) limit = 30;
	try {
		let groupMemberRequest = new CometChat.GroupMembersRequestBuilder(groupId)
			.setLimit(limit)
			.setScopes(scope)
			.build();
		const group = await groupMemberRequest.fetchNext();
		return group;
	} catch (error) {
		console.error("Error fetching group metadata", error);
		return null;
	}
}


export const messageListStyle = new MessageListStyle({
	background: "transparent",
	height: "100%",
	width: "100%",
	loadingIconTint: "#8a33f0",
});

export const dateStyle = new DateStyle({
	textColor: '#757575',
	textFont: "500 13px Manrope",
	background: '#7575751F',
	borderRadius: "6px",
})